import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const when = (ts) => new Date(ts).toLocaleString();

function ImportRow({ imp, onDone }) {
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const call = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/products/imports/${imp.id}/undo`, { dry_run: dry });
      if (dry) setPlan(data);
      else { toast.success(`Removed ${data.removable} duplicate product(s)`); setPlan(null); onDone(); }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border p-3 text-sm space-y-2" data-testid={`import-row-${imp.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1"><b>{when(imp.timestamp)}</b> · {imp.actor_name} · {imp.created} product(s) created
          {imp.undone && <span className="ml-2 rounded bg-muted px-1.5 text-xs">undone</span>}</div>
        {!plan && <Button size="sm" variant="outline" disabled={busy} onClick={() => call(true)} data-testid={`import-undo-btn-${imp.id}`}>Remove duplicates…</Button>}
      </div>
      {plan && (
        <div className="rounded bg-rose-50 border border-rose-200 p-2 space-y-2" data-testid={`import-undo-plan-${imp.id}`}>
          <div><b>{plan.removable}</b> duplicate product(s) from this import will be deleted.{plan.kept_in_use > 0 && <> {plan.kept_in_use} will be kept because they already have stock or sales.</>}</div>
          {plan.sample.length > 0 && <ul className="text-xs text-muted-foreground max-h-32 overflow-y-auto">{plan.sample.map((s) => <li key={s.sku}>{s.name} <span className="font-mono">{s.sku}</span></li>)}</ul>}
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" disabled={busy || !plan.removable} onClick={() => call(false)} data-testid={`import-undo-confirm-${imp.id}`}>{busy ? "Removing…" : `Delete ${plan.removable} product(s)`}</Button>
            <Button size="sm" variant="ghost" onClick={() => setPlan(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Super Admin: recent CSV imports, with undo (removes created products that are not in use).
export function ImportHistoryDialog({ open, onOpenChange, onChanged }) {
  const [rows, setRows] = useState(null);
  const load = () => { api.get("/products/imports").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(() => { if (open) { load(); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-history-dialog">
        <DialogHeader>
          <DialogTitle>Recent CSV imports</DialogTitle>
          <DialogDescription>Remove the copies an import created of products you already had (same name, strength, form and brand). Genuinely new products, and anything with stock, sales or other history, are kept.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          {!rows && <div className="text-sm text-muted-foreground">Loading…</div>}
          {rows && rows.length === 0 && <div className="text-sm text-muted-foreground">No imports yet</div>}
          {rows?.map((imp) => <ImportRow key={imp.id} imp={imp} onDone={() => { load(); onChanged(); }} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
